import { useMemo } from 'react';
import {
  createAttendanceFeatureBundle,
  createMockAttendanceTransport,
  type AttendanceFeatureBundle,
  type AttendanceTransport,
} from '@triedge/attendance-ui';
import type {
  AttendanceContextExportInput,
  AttendanceCsvExportInput,
  AttendanceMutationArgsMap,
  AttendanceMutationOperationId,
  AttendanceMutationResultMap,
  AttendanceQueryArgsMap,
  AttendanceQueryOperationId,
  AttendanceQueryResultMap,
} from '@triedge/attendance-domain/router-contract';
import type { AttendanceScope } from '@triedge/attendance-domain';
import { domainRouterClient } from '../api/domainRouterClient';
import { useSchoolContext } from '../state/useSchoolContext';

const ATTENDANCE_ROUTE_BASE = '/school/attendance';
const STUDENTS_FAMILIES_ROUTE_BASE = '/school/students';
const LOCAL_FIXTURE_FLAG = 'ystnj.attendance.v12.fixture';

type AttendanceAppScope = AttendanceScope;

const queryOperationIds = new Set<string>([
  'getAttendanceCaptureContext',
  'getAttendanceOverview',
  'listAttendanceRecords',
  'getAttendanceStudentHistory',
  'getAttendanceControlPlaneConfig',
  'listAttendanceAuditEvents',
  'getAttendanceImportStatus',
]);

const mutationOperationIds = new Set<string>([
  'saveAttendanceRecordCell',
  'applyAttendanceBulkAction',
  'submitAttendanceRecordScope',
  'resetAttendanceRecordScope',
  'updateAttendanceControlPlaneConfig',
  'requestAttendanceImportUpload',
  'startAttendanceImport',
  'exportAttendanceRecordsCsv',
  'exportAttendanceContext',
]);

const isAttendanceQueryOperation = (name: string): name is AttendanceQueryOperationId =>
  queryOperationIds.has(name);

const isAttendanceMutationOperation = (name: string): name is AttendanceMutationOperationId =>
  mutationOperationIds.has(name);

const SCHOOL_TIME_ZONE = 'America/New_York';

const todayIso = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: SCHOOL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const partByType = new Map(parts.map((part) => [part.type, part.value]));
  return `${partByType.get('year')}-${partByType.get('month')}-${partByType.get('day')}`;
};

const scopeFromSchoolContext = (
  schoolContext: ReturnType<typeof useSchoolContext>,
): AttendanceAppScope | null => {
  const activeSchool = schoolContext.schools?.find(
    (school) => school.schoolId === schoolContext.activeSchoolId,
  );
  if (!activeSchool?.tenantId || !activeSchool.schoolId) return null;

  return {
    organizationId: activeSchool.tenantId,
    schoolId: activeSchool.schoolId,
  };
};

const withScope = <TInput extends Record<string, unknown>>(
  scope: AttendanceAppScope,
  input: TInput,
): TInput & AttendanceAppScope => ({
  ...input,
  organizationId: scope.organizationId,
  schoolId: scope.schoolId,
});

const ensureSchoolDate = (value: unknown): string =>
  typeof value === 'string' && value.trim() ? value : todayIso();

const ensureDayMode = (value: unknown): 'day' | 'week' =>
  value === 'week' ? 'week' : 'day';

const ensureOverviewWindow = (value: unknown): 7 | 30 | 60 | 90 =>
  value === 7 || value === 30 || value === 60 || value === 90 ? value : 30;

const normalizeQueryArgs = <Name extends AttendanceQueryOperationId>(
  scope: AttendanceAppScope,
  name: Name,
  args: AttendanceQueryArgsMap[Name],
): AttendanceQueryArgsMap[Name] => {
  const input = ((args as { input?: Record<string, unknown> })?.input ?? {}) as Record<string, unknown>;

  if (name === 'listAttendanceRecords') {
    return {
      input: withScope(scope, {
        ...input,
        schoolDate: ensureSchoolDate(input.schoolDate),
        dayMode: ensureDayMode(input.dayMode),
      }),
    } as AttendanceQueryArgsMap[Name];
  }

  if (name === 'getAttendanceOverview') {
    return {
      input: withScope(scope, {
        ...input,
        schoolDate: ensureSchoolDate(input.schoolDate),
        rollingSchoolDayWindow: ensureOverviewWindow(input.rollingSchoolDayWindow),
      }),
    } as AttendanceQueryArgsMap[Name];
  }

  if (name === 'getAttendanceStudentHistory') {
    return {
      input: withScope(scope, {
        ...input,
        fromSchoolDate: ensureSchoolDate(input.fromSchoolDate),
        toSchoolDate: ensureSchoolDate(input.toSchoolDate),
      }),
    } as AttendanceQueryArgsMap[Name];
  }

  return {
    input: withScope(scope, input),
  } as AttendanceQueryArgsMap[Name];
};

const normalizeMutationArgs = <Name extends AttendanceMutationOperationId>(
  scope: AttendanceAppScope,
  name: Name,
  args: AttendanceMutationArgsMap[Name],
): AttendanceMutationArgsMap[Name] => {
  const input = ((args as { input?: Record<string, unknown> })?.input ?? {}) as Record<string, unknown>;

  if (name === 'exportAttendanceRecordsCsv') {
    const exportInput = input as AttendanceCsvExportInput;
    if (exportInput.scope === 'records-current-filter') {
      return {
        input: {
          ...exportInput,
          recordsFilter: withScope(scope, {
            ...exportInput.recordsFilter,
            schoolDate: ensureSchoolDate(exportInput.recordsFilter.schoolDate),
            dayMode: ensureDayMode(exportInput.recordsFilter.dayMode),
          }),
        },
      } as AttendanceMutationArgsMap[Name];
    }
    if (exportInput.scope === 'overview-current-date') {
      return {
        input: withScope(scope, {
          ...exportInput,
          schoolDate: ensureSchoolDate(exportInput.schoolDate),
          rollingSchoolDayWindow: ensureOverviewWindow(exportInput.rollingSchoolDayWindow),
        }),
      } as AttendanceMutationArgsMap[Name];
    }
  }

  if (name === 'exportAttendanceContext') {
    const exportInput = input as AttendanceContextExportInput;
    if (
      (exportInput.context === 'new-records' || exportInput.context === 'teacher-view') &&
      exportInput.recordsFilter
    ) {
      return {
        input: {
          ...exportInput,
          recordsFilter: withScope(scope, {
            ...exportInput.recordsFilter,
            schoolDate: ensureSchoolDate(exportInput.recordsFilter.schoolDate),
            dayMode: ensureDayMode(exportInput.recordsFilter.dayMode),
          }),
        },
      } as AttendanceMutationArgsMap[Name];
    }
    if (exportInput.context === 'overview') {
      return {
        input: withScope(scope, {
          ...exportInput,
          schoolDate: ensureSchoolDate(exportInput.schoolDate),
          rollingSchoolDayWindow: ensureOverviewWindow(exportInput.rollingSchoolDayWindow),
        }),
      } as AttendanceMutationArgsMap[Name];
    }
  }

  if (
    name === 'applyAttendanceBulkAction'
    || name === 'resetAttendanceRecordScope'
    || name === 'submitAttendanceRecordScope'
  ) {
    return {
      input: withScope(scope, {
        ...input,
        schoolDate: ensureSchoolDate(input.schoolDate),
        dayMode: ensureDayMode(input.dayMode),
      }),
    } as AttendanceMutationArgsMap[Name];
  }

  return {
    input: withScope(scope, input),
  } as AttendanceMutationArgsMap[Name];
};

const isLocalFixtureEnabled = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') return false;
  const fixtureParam = new URLSearchParams(window.location.search).get('attendanceFixture');
  if (fixtureParam === '0') {
    return false;
  }
  if (fixtureParam === '1') {
    return true;
  }
  const fixturePreference = window.localStorage?.getItem(LOCAL_FIXTURE_FLAG);
  if (fixturePreference === '0') {
    return false;
  }
  if (fixturePreference === '1') {
    return true;
  }
  return window.location.pathname.startsWith(ATTENDANCE_ROUTE_BASE);
};

const createAttendanceTransport = (scope: AttendanceAppScope): AttendanceTransport => {
  const fixtureTransport = createMockAttendanceTransport({
    scope,
    studentsFamiliesRouteBase: STUDENTS_FAMILIES_ROUTE_BASE,
    persistenceKey: `${LOCAL_FIXTURE_FLAG}:${scope.organizationId}:${scope.schoolId}`,
  });

  return {
    async query<Name extends AttendanceQueryOperationId>(
      name: Name,
      args: AttendanceQueryArgsMap[Name],
    ): Promise<AttendanceQueryResultMap[Name]> {
      if (!isAttendanceQueryOperation(name)) {
        throw new Error(`Unsupported Attendance query operation "${name}".`);
      }

      if (isLocalFixtureEnabled()) {
        return fixtureTransport.query(name, args);
      }

      return domainRouterClient.query(
        name,
        normalizeQueryArgs(scope, name, args) as never,
      ) as Promise<AttendanceQueryResultMap[Name]>;
    },

    async mutate<Name extends AttendanceMutationOperationId>(
      name: Name,
      args: AttendanceMutationArgsMap[Name],
    ): Promise<AttendanceMutationResultMap[Name]> {
      if (!isAttendanceMutationOperation(name)) {
        throw new Error(`Unsupported Attendance mutation operation "${name}".`);
      }

      if (isLocalFixtureEnabled()) {
        const result = await fixtureTransport.mutate(name, args);
        window.dispatchEvent(
          new CustomEvent('ystnj-attendance-fixture-operation', {
            detail: { name, args, result },
          }),
        );
        return result;
      }

      return domainRouterClient.mutate(
        name,
        normalizeMutationArgs(scope, name, args) as never,
      ) as Promise<AttendanceMutationResultMap[Name]>;
    },
  };
};

export const createYstnjAttendanceFeatureBundle = (
  scope: AttendanceAppScope,
): AttendanceFeatureBundle =>
  createAttendanceFeatureBundle({
    routeBase: ATTENDANCE_ROUTE_BASE,
    studentsFamiliesRouteBase: STUDENTS_FAMILIES_ROUTE_BASE,
    transport: createAttendanceTransport(scope),
    scope,
    extractedFrom: 'package:@triedge/attendance-ui:v1.2-ystnj-adapter',
  });

export const useYstnjAttendanceFeatureBundleState = (): {
  bundle: AttendanceFeatureBundle | null;
  error: string | null;
  isLoading: boolean;
  needsSelection: boolean;
} => {
  const schoolContext = useSchoolContext();
  const scope = useMemo(
    () => scopeFromSchoolContext(schoolContext),
    [schoolContext.activeSchoolId, schoolContext.schools],
  );
  const bundle = useMemo(
    () => (scope ? createYstnjAttendanceFeatureBundle(scope) : null),
    [scope?.organizationId, scope?.schoolId],
  );

  return {
    bundle,
    error: schoolContext.error,
    isLoading: schoolContext.isLoading || schoolContext.isResolving,
    needsSelection: schoolContext.needsSelection || !scope,
  };
};

export const useYstnjAttendanceFeatureBundle = (): AttendanceFeatureBundle | null =>
  useYstnjAttendanceFeatureBundleState().bundle;
